import { getAppModuleLabelForPath } from '@/config/appNavigation'
import {
  buildRouteCatalog,
  collectCatalogPages,
  titleCaseSegment,
  type RouteConfigLike,
  type RouteNode,
} from './routeCatalog'
import { findRouteParamSource } from './routeParamSources'

/**
 * The catalog for a router config: its pages whose parameters all have a
 * source; with `includePublic`, the signed-out pages too.
 */
export function createRouteCatalog(
  routes: readonly RouteConfigLike[],
  { includePublic = false }: { includePublic?: boolean } = {}
): RouteNode {
  return buildRouteCatalog(
    collectCatalogPages(routes, '/', { includePublic }),
    (paramPattern) => findRouteParamSource(paramPattern) !== undefined
  )
}

/** The first segments of the signed-out pages (`sign-in`), in route order. */
export function publicTopLevelSegments(routes: readonly RouteConfigLike[]): string[] {
  const segments = collectCatalogPages(routes, '/', { includePublic: true })
    .filter((page) => page.isPublic)
    .map((page) => page.pattern.split('/')[1] ?? '')
  return [...new Set(segments.filter(Boolean))]
}

/** Fixed segments whose name isn't their title-cased segment. */
const SEGMENT_LABELS: Readonly<Record<string, string>> = {
  'table-views': 'Views',
}

/** A fixed segment's name: an area by its navigation label, anything else by its segment. */
export function routeSegmentLabel(node: RouteNode) {
  if (node.pattern.lastIndexOf('/') === 0) {
    return getAppModuleLabelForPath(node.pattern) ?? titleCaseSegment(node.segment)
  }
  return SEGMENT_LABELS[node.segment] ?? titleCaseSegment(node.segment)
}

/** The name of the choice that stays on a page that has pages below it. */
export function routeLandingLabel(root: RouteNode, node: RouteNode) {
  if (node === root) return getAppModuleLabelForPath('/') ?? 'Home'
  return node.landing || 'Default'
}
